import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useEscapeClose from '../hooks/useEscapeClose';
import { API_URL } from '../config/api';
import {
  ArrowLeft, Save, RotateCcw, Trash2, Copy, ZoomIn, ZoomOut,
  Type, Bold, AlignLeft, AlignCenter, AlignRight, Tag, Printer,
  Move, Layers, Settings, ChevronDown, ChevronUp,
  Hash, Scale, Coins, DollarSign, FolderOpen, Award, ClipboardList,
  Pencil, BarChart2, Grip, Plus, FileText, Check, X, Edit3, TrendingUp, Truck
} from 'lucide-react';
import { handlePrint } from '../utils/printHelper';
import JsBarcode from 'jsbarcode';
import {
  LABEL_EL_CLASS, getLabelPrintOffset, labelShiftScript, getBarcodeModuleMm, labelBarcodeSizeScript, DEFAULT_BARCODE_MODULE_MM, getLabelPageHeightIn, labelElementCss,
  LABEL_AREA_KEYS, LABEL_AREA_DIM_KEY, getLabelAreaOffset, getLabelAreaPrintOffset, getLabelAlign, labelAlignJustify, labelBarcodeOptions, getLabelBarcodeFormat,
} from '../utils/labelPrintOffset';
import {
  checkZebraAvailable, ensureZebraAvailable, listZebraPrinters, getZebraSettings, saveZebraSettings, printLabelsZebra,
  renderLabelCanvas, buildCalibrationZpl, sendZpl, getZebraPrinter, PRINT_APP_DOWNLOAD_URL,
  PRINTER_MODELS, getDeviceShiftMm,
} from '../utils/zebraLabel';

// === ADOBE-STYLE PROFESSIONAL THEME ===
const T = {
  bg: '#1e1e1e',
  panel: '#2b2b2b',
  panelLight: '#323232',
  panelDark: '#252525',
  border: '#3c3c3c',
  borderLight: '#484848',
  canvas: '#1a1a1a',
  canvasDot: '#2a2a2a',
  headerBg: '#2b2b2b',
  headerBorder: '#3c3c3c',
  accent: '#2680eb',
  accentHover: '#1a73e8',
  accentBg: 'rgba(38,128,235,0.12)',
  accentBorder: 'rgba(38,128,235,0.35)',
  text: '#e6e6e6',
  textSec: '#999999',
  textDim: '#666666',
  inputBg: '#1e1e1e',
  inputBorder: '#3c3c3c',
  inputFocus: '#2680eb',
  danger: '#e34850',
  dangerBg: 'rgba(227,72,80,0.12)',
  dangerBorder: 'rgba(227,72,80,0.35)',
  success: '#2680eb',
  gold: '#e8a830',
  silver: '#94a3b8',
};

// Türkçe karakter ve baskı kalitesi için en uygun fontlar
const LABEL_FONTS = [
  { id: 'Arial', label: 'Arial', css: "Arial, Helvetica, sans-serif", google: false },
  { id: 'Roboto Condensed', label: 'Roboto Condensed', css: "'Roboto Condensed', Arial, sans-serif", google: true },
  { id: 'Inter', label: 'Inter', css: "'Inter', Arial, sans-serif", google: true },
  { id: 'Oswald', label: 'Oswald', css: "'Oswald', Arial, sans-serif", google: true },
  { id: 'Noto Sans', label: 'Noto Sans', css: "'Noto Sans', Arial, sans-serif", google: true },
  { id: 'Montserrat', label: 'Montserrat', css: "'Montserrat', Arial, sans-serif", google: true },
];

// Google Fonts import URL
const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Montserrat:wght@400;600;700&family=Noto+Sans:wght@400;600;700&family=Oswald:wght@400;600;700&family=Roboto+Condensed:wght@400;700&display=swap';

// Available field types
const FIELD_TYPES = [
  { id: 'brand', label: 'Firma/Marka', Icon: Tag, defaultContent: 'Akik Gold Jewellery', defaultArea: 'left', defaultFontSize: 5, defaultFontWeight: 'bold' },
  { id: 'barcode', label: 'Barkod', Icon: BarChart2, defaultContent: 'BARCODE', defaultArea: 'left', defaultFontSize: 5, isBarcode: true },
  { id: 'barcodeNumber', label: 'Barkod No', Icon: Hash, defaultContent: '01010169', defaultArea: 'left', defaultFontSize: 5, defaultFontWeight: 'bold' },
  { id: 'weightPurity', label: 'Ağırlık - Ayar', Icon: Scale, defaultContent: '4.01 - 14K', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'has', label: 'HAS Değeri', Icon: Coins, defaultContent: 'Has: 1.58', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'silverHas', label: 'Gümüş HAS (Çarpanlı)', Icon: Coins, defaultContent: '28.47', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'iscilik', label: 'İşçilik', Icon: Coins, defaultContent: 'İşç: 750.00', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'laboredGram', label: 'İşçilikli Gram', Icon: Scale, defaultContent: '3.618H', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'laborCost', label: 'İşçilik Maliyeti ($)', Icon: DollarSign, defaultContent: 'İşç: 3.00$', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'usd', label: 'USD Değeri', Icon: DollarSign, defaultContent: '2550 - USD', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'gram', label: 'GRAM', Icon: Scale, defaultContent: '2.710 GR', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'category', label: 'Kategori', Icon: FolderOpen, defaultContent: 'Küpe', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'weight', label: 'Sadece Ağırlık', Icon: Scale, defaultContent: '2.710', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'purity', label: 'Sadece Ayar', Icon: Award, defaultContent: '14k', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'productName', label: 'Ürün Adı', Icon: Type, defaultContent: 'Gümüş Yüzük', defaultArea: 'info', defaultFontSize: 6, defaultFontWeight: 'bold' },
  { id: 'stockCode', label: 'Stok Kodu', Icon: ClipboardList, defaultContent: 'STK001', defaultArea: 'info', defaultFontSize: 4, defaultFontWeight: 'normal' },
  { id: 'laborMultiplied', label: 'İşçilikli Çarpan', Icon: TrendingUp, defaultContent: '25.62i', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'profitMultiplier', label: 'Kat Sayısı', Icon: TrendingUp, defaultContent: 'x3.00', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'price', label: 'Ürün Fiyatı', Icon: DollarSign, defaultContent: '1.250 ₺', defaultArea: 'info', defaultFontSize: 7, defaultFontWeight: 'bold' },
  { id: 'productColor', label: 'Renk', Icon: Pencil, defaultContent: 'Altın', defaultArea: 'info', defaultFontSize: 6, defaultFontWeight: 'bold' },
  { id: 'productSize', label: 'Ölçü/Boyut', Icon: Scale, defaultContent: '18', defaultArea: 'info', defaultFontSize: 6, defaultFontWeight: 'bold' },
  { id: 'supplier', label: 'Tedarikçi', Icon: Truck, defaultContent: 'Tedarikçi', defaultArea: 'info', defaultFontSize: 6, defaultFontWeight: 'bold' },
  { id: 'customText', label: 'Özel Metin', Icon: Pencil, defaultContent: 'Metin', defaultArea: 'any', defaultFontSize: 5, defaultFontWeight: 'normal' },
];

const CURRENCY_SYMBOLS = [
  { id: 'TL', symbol: '₺', label: '₺ TL' },
  { id: 'EUR', symbol: '€', label: '€ Euro' },
  { id: 'USD', symbol: '$', label: '$ Dolar' },
  { id: 'GBP', symbol: '£', label: '£ Sterlin' },
];

// Etiket üzerindeki 4 baskı bölgesi soldan sağa bu sırayla dizili (Sol Baskı | Katlama | Bilgi | Kuyruk).
// Elemanlar artık bu bölgelerin herhangi birine serbestçe yerleştirilebiliyor - eskiden
// sadece Sol/Bilgi'ye izin veriliyordu.
const AREA_KEYS = LABEL_AREA_KEYS;
const AREA_LABELS = { left: 'Sol', fold: 'Kat', info: 'Bilgi', tail: 'Kuyruk' };
const AREA_DIM_KEY = LABEL_AREA_DIM_KEY;
const getAreaWidth = (area, dm) => dm?.[AREA_DIM_KEY[area]] || 0;
const getAreaOffset = getLabelAreaOffset;

const createDefaultDesign = (type, id, name) => ({
  id: id || (type === 'gold' ? 'gold-default' : 'silver-default'),
  name: name || (type === 'gold' ? 'Varsayilan Altın' : 'Varsayilan Gümüş'),
  type,
  dimensions: {
    labelWidth: 70, labelHeight: 9,
    leftPrintAreaWidth: 20, foldAreaWidth: 3,
    infoPrintAreaWidth: 16, tailAreaWidth: 31,
  },
  elements: [
    { id: 'el-1', field: 'brand', area: 'left', x: 0, y: 0.3, width: 20, height: 2, fontSize: 5, fontWeight: 'bold', textAlign: 'center', content: type === 'gold' ? 'Akik Gold Jewellery' : 'Akik Silver' },
    { id: 'el-2', field: 'barcode', area: 'left', x: 0, y: 2.5, width: 20, height: 3.5, fontSize: 5, fontWeight: 'normal', textAlign: 'center', content: 'BARCODE', isBarcode: true },
    { id: 'el-3', field: 'barcodeNumber', area: 'left', x: 0, y: 6.5, width: 20, height: 2, fontSize: 5, fontWeight: 'bold', textAlign: 'center', content: '01010169' },
    { id: 'el-4', field: 'weightPurity', area: 'info', x: 0, y: 0.5, width: 16, height: 2.5, fontSize: 7, fontWeight: 'bold', textAlign: 'center', content: type === 'gold' ? '4.01 - 14K' : '12.50 - 925' },
    { id: 'el-5', field: 'has', area: 'info', x: 0, y: 3, width: 16, height: 2.5, fontSize: 7, fontWeight: 'bold', textAlign: 'center', content: '12.03 - HAS' },
    { id: 'el-6', field: 'category', area: 'info', x: 0, y: 5.5, width: 16, height: 3, fontSize: 7, fontWeight: 'bold', textAlign: 'center', content: 'Kupe' },
  ],
  settings: {
    printerModel: 'Zebra ZD220', printerDpi: 203,
    barcodeHeight: 3.5, barcodeWidth: 1.0,
    defaultBrand: type === 'gold' ? 'Akik Gold Jewellery' : 'Akik Silver',
    defaultPurity: type === 'gold' ? '14k' : '925',
    defaultProfitMargin: 3,
  },
});

const getDefaultDesigns = () => ({
  activeGoldId: 'gold-default',
  activeSilverId: 'silver-default',
  templates: {
    'gold-default': createDefaultDesign('gold'),
    'silver-default': createDefaultDesign('silver'),
  }
});

const LabelDesign = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(12);

  // Kullanıcının mağaza tipi ve yetki kontrolü
  const currentStoreId = selectedStore?.id || user?.store_id;
  const isAdmin = user?.role === 'admin';
  const storeMetalType = currentStoreId === 1 ? 'gold' : 'silver';

  // Google Fonts yükle
  useEffect(() => {
    if (!document.getElementById('label-google-fonts')) {
      const link = document.createElement('link');
      link.id = 'label-google-fonts';
      link.rel = 'stylesheet';
      link.href = GOOGLE_FONTS_URL;
      document.head.appendChild(link);
    }
  }, []);
  const [designType, setDesignType] = useState(() => isAdmin ? 'gold' : storeMetalType);
  const [designs, setDesigns] = useState(getDefaultDesigns);
  const [selectedId, setSelectedId] = useState(null);
  const [dragInfo, setDragInfo] = useState(null);
  const [showDimensions, setShowDimensions] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [loading, setLoading] = useState(true);

  // API'den tasarımları yükle
  const loadDesignsFromAPI = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const typeParam = isAdmin ? '' : `?type=${storeMetalType}`;
      const res = await fetch(`${API_URL}/label-designs${typeParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.designs.length > 0) {
        const templates = {};
        data.designs.forEach(d => {
          templates[d.id] = d;
        });
        // Varsayılanlar yoksa ekle (sadece erişim olan tipler için)
        if (isAdmin || storeMetalType === 'gold') {
          if (!templates['gold-default']) templates['gold-default'] = createDefaultDesign('gold');
        }
        if (isAdmin || storeMetalType === 'silver') {
          if (!templates['silver-default']) templates['silver-default'] = createDefaultDesign('silver');
        }
        setDesigns({
          activeGoldId: data.activeGoldId || 'gold-default',
          activeSilverId: data.activeSilverId || 'silver-default',
          templates,
        });
      } else {
        // DB boşsa localStorage'dan migration yap
        try {
          const saved = localStorage.getItem('labelDesignTemplates');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.templates && Object.keys(parsed.templates).length > 0) {
              if (!parsed.templates['gold-default']) parsed.templates['gold-default'] = createDefaultDesign('gold');
              if (!parsed.templates['silver-default']) parsed.templates['silver-default'] = createDefaultDesign('silver');
              setDesigns({
                activeGoldId: parsed.activeGoldId || 'gold-default',
                activeSilverId: parsed.activeSilverId || 'silver-default',
                templates: parsed.templates,
              });
              return;
            }
          }
        } catch (e) {}
        setDesigns(getDefaultDesigns());
      }
    } catch (error) {
      console.error('Tasarımlar yüklenirken hata:', error);
      // Hata durumunda localStorage'dan yükle
      try {
        const saved = localStorage.getItem('labelDesignTemplates');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.templates) {
            setDesigns({
              activeGoldId: parsed.activeGoldId || 'gold-default',
              activeSilverId: parsed.activeSilverId || 'silver-default',
              templates: parsed.templates,
            });
            return;
          }
        }
      } catch (e) {}
      setDesigns(getDefaultDesigns());
    } finally {
      setLoading(false);
    }
  }, [isAdmin, storeMetalType]);

  useEffect(() => {
    loadDesignsFromAPI();
  }, [loadDesignsFromAPI]);

  useEscapeClose([
    [showTemplates, () => setShowTemplates(false)],
    [showDimensions, () => setShowDimensions(false)],
  ]);

  const templateDropdownRef = useRef(null);
  const elCounter = useRef(100);
  const designTypeRef = useRef(designType);
  designTypeRef.current = designType;

  // Active template ID for current type
  const designId = designType === 'gold' ? (designs.activeGoldId || 'gold-default') : (designs.activeSilverId || 'silver-default');
  const design = designs.templates[designId] || createDefaultDesign(designType);
  const dim = design.dimensions;
  const elements = design.elements || [];
  const settings = design.settings || {};
  const selected = selectedId ? elements.find(el => el.id === selectedId) : null;

  // Templates for current type
  const typeTemplates = Object.values(designs.templates).filter(t => t.type === designType);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setShowTemplates(false);
        setRenamingId(null);
      }
    };
    if (showTemplates) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTemplates]);

  // ---- Helpers ----
  const _id = (prev) => {
    const t = designTypeRef.current;
    return t === 'gold' ? (prev.activeGoldId || 'gold-default') : (prev.activeSilverId || 'silver-default');
  };
  const _update = (fn) => {
    setDesigns(prev => {
      const id = _id(prev);
      const d = prev.templates[id];
      if (!d) return prev;
      return { ...prev, templates: { ...prev.templates, [id]: fn(d) } };
    });
  };

  // ---- Plain functions, NO useCallback - zero closure issues ----
  const updateEl = (elId, updates) => {
    setDesigns(prev => {
      const id = _id(prev);
      const d = prev.templates[id];
      if (!d) return prev;
      return {
        ...prev,
        templates: {
          ...prev.templates,
          [id]: { ...d, elements: d.elements.map(el => el.id === elId ? { ...el, ...updates } : el) }
        }
      };
    });
  };

  const removeEl = (elId) => {
    setDesigns(prev => {
      const id = _id(prev);
      const d = prev.templates[id];
      if (!d) return prev;
      return {
        ...prev,
        templates: {
          ...prev.templates,
          [id]: { ...d, elements: d.elements.filter(el => el.id !== elId) }
        }
      };
    });
    setSelectedId(prev => prev === elId ? null : prev);
  };

  const addEl = (fieldType, area, dropX, dropY) => {
    const fd = FIELD_TYPES.find(f => f.id === fieldType);
    if (!fd) return;
    const newId = `el-${Date.now()}-${elCounter.current++}`;
    setDesigns(prev => {
      const id = _id(prev);
      const d = prev.templates[id] || createDefaultDesign(designTypeRef.current);
      const areaW = getAreaWidth(area, d.dimensions);
      const newEl = {
        id: newId, field: fieldType, area,
        x: dropX,
        y: dropY,
        width: areaW,
        height: fieldType === 'barcode' ? (d.settings?.barcodeHeight || 3.5) : 2,
        fontSize: fd.defaultFontSize || 5,
        fontWeight: fd.defaultFontWeight || 'bold',
        textAlign: 'center',
        content: fd.defaultContent,
        isBarcode: fd.isBarcode || false,
        ...(fieldType === 'price' ? { currencySymbol: '', showCurrency: true } : {}),
      };
      return { ...prev, templates: { ...prev.templates, [id]: { ...d, elements: [...d.elements, newEl] } } };
    });
    setSelectedId(newId);
  };

  const duplicateEl = (elId) => {
    const newId = `el-${Date.now()}-${elCounter.current++}`;
    setDesigns(prev => {
      const id = _id(prev);
      const d = prev.templates[id];
      if (!d) return prev;
      const el = d.elements.find(e => e.id === elId);
      if (!el) return prev;
      const newEl = { ...el, id: newId, y: el.y + 1.5 };
      return { ...prev, templates: { ...prev.templates, [id]: { ...d, elements: [...d.elements, newEl] } } };
    });
    setSelectedId(newId);
  };

  // ---- Template management ----
  const switchTemplate = (templateId) => {
    setDesigns(prev => ({
      ...prev,
      ...(designType === 'gold' ? { activeGoldId: templateId } : { activeSilverId: templateId })
    }));
    setSelectedId(null);
    setShowTemplates(false);
  };

  const createNewTemplate = () => {
    const newId = `${designType}-${Date.now()}`;
    const name = `${designType === 'gold' ? 'Altın' : 'Gümüş'} Taslak ${typeTemplates.length}`;
    const newTemplate = createDefaultDesign(designType, newId, name);
    setDesigns(prev => ({
      ...prev,
      templates: { ...prev.templates, [newId]: newTemplate },
      ...(designType === 'gold' ? { activeGoldId: newId } : { activeSilverId: newId })
    }));
    setSelectedId(null);
    setShowTemplates(false);
    toast.success(`"${name}" olusturuldu`);
  };

  const duplicateTemplate = () => {
    const newId = `${designType}-${Date.now()}`;
    const currentName = design.name || 'Taslak';
    const newName = `${currentName} (Kopya)`;
    const cloned = JSON.parse(JSON.stringify(design));
    cloned.id = newId;
    cloned.name = newName;
    // Generate new element IDs
    cloned.elements = cloned.elements.map((el, i) => ({ ...el, id: `el-${Date.now()}-${i}` }));
    setDesigns(prev => ({
      ...prev,
      templates: { ...prev.templates, [newId]: cloned },
      ...(designType === 'gold' ? { activeGoldId: newId } : { activeSilverId: newId })
    }));
    setSelectedId(null);
    setShowTemplates(false);
    toast.success(`"${newName}" olusturuldu`);
  };

  const deleteTemplate = async (templateId) => {
    if (templateId === 'gold-default' || templateId === 'silver-default') {
      toast.warning('Varsayilan taslak silinemez');
      return;
    }
    const templateName = designs.templates[templateId]?.name || 'Taslak';
    if (!window.confirm(`"${templateName}" taslagi silinsin mi?`)) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/label-designs/${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { /* DB'den silinemezse bile local'den sil */ }
    setDesigns(prev => {
      const newTemplates = { ...prev.templates };
      delete newTemplates[templateId];
      const fallbackId = designType === 'gold' ? 'gold-default' : 'silver-default';
      return {
        ...prev,
        templates: newTemplates,
        ...(designType === 'gold'
          ? { activeGoldId: prev.activeGoldId === templateId ? fallbackId : prev.activeGoldId }
          : { activeSilverId: prev.activeSilverId === templateId ? fallbackId : prev.activeSilverId }
        )
      };
    });
    setSelectedId(null);
    toast.info(`"${templateName}" silindi`);
  };

  const startRename = (templateId) => {
    setRenamingId(templateId);
    setRenameValue(designs.templates[templateId]?.name || '');
  };

  const confirmRename = () => {
    if (!renamingId || !renameValue.trim()) return;
    setDesigns(prev => ({
      ...prev,
      templates: {
        ...prev.templates,
        [renamingId]: { ...prev.templates[renamingId], name: renameValue.trim() }
      }
    }));
    setRenamingId(null);
    setRenameValue('');
  };

  // ---- Drag on canvas (uses refs for always-fresh values) ----
  const dragRef = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const handleMouseDown = (e, elId) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(elId);
    const el = elements.find(el => el.id === elId);
    if (!el) return;
    const info = { elId, sx: e.clientX, sy: e.clientY, ox: el.x, oy: el.y };
    dragRef.current = info;
    setDragInfo(info);
  };

  useEffect(() => {
    if (!dragInfo) return;
    const onMove = (e) => {
      const di = dragRef.current;
      if (!di) return;
      const z = zoomRef.current;
      const dx = (e.clientX - di.sx) / z;
      const dy = (e.clientY - di.sy) / z;
      setDesigns(prev => {
        const id = _id(prev);
        const d = prev.templates[id];
        if (!d) return prev;
        const el = d.elements.find(el => el.id === di.elId);
        if (!el) return prev;
        // Sınırlama yok - eleman etiketin/bölgenin dışına da taşınabilir (tam esneklik isteniyor)
        const newX = Math.round((di.ox + dx) * 4) / 4;
        const newY = Math.round((di.oy + dy) * 4) / 4;
        if (el.x === newX && el.y === newY) return prev;
        return {
          ...prev,
          templates: {
            ...prev.templates,
            [id]: { ...d, elements: d.elements.map(el => el.id === di.elId ? { ...el, x: newX, y: newY } : el) }
          }
        };
      });
    };
    const onUp = () => { dragRef.current = null; setDragInfo(null); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Sidebar drag & drop ----
  const onSidebarDragStart = (e, fieldId) => {
    e.dataTransfer.setData('fieldId', fieldId);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const onAreaDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const onAreaDrop = (e, area) => {
    e.preventDefault();
    const fieldId = e.dataTransfer.getData('fieldId');
    if (!fieldId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    addEl(fieldId, area, (e.clientX - rect.left) / zoom, (e.clientY - rect.top) / zoom);
  };

  // ---- Save / Reset ----
  const saveDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const allTemplates = Object.values(designs.templates);
      const res = await fetch(`${API_URL}/label-designs/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          designs: allTemplates,
          activeGoldId: designs.activeGoldId,
          activeSilverId: designs.activeSilverId,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);

      // Backward-compatible localStorage settings (yazdırma için)
      const d = designs.templates[designId];
      if (d) {
        const cs = {
          labelWidth: d.dimensions.labelWidth, labelHeight: d.dimensions.labelHeight,
          leftPrintAreaWidth: d.dimensions.leftPrintAreaWidth, printAreaWidth: d.dimensions.leftPrintAreaWidth,
          foldAreaWidth: d.dimensions.foldAreaWidth, middleAreaWidth: d.dimensions.foldAreaWidth,
          infoPrintAreaWidth: d.dimensions.infoPrintAreaWidth, rightPrintAreaWidth: d.dimensions.infoPrintAreaWidth,
          tailAreaWidth: d.dimensions.tailAreaWidth, handleAreaWidth: d.dimensions.tailAreaWidth,
          printerDpi: d.settings.printerDpi, printerModel: d.settings.printerModel,
          barcodeHeight: d.settings.barcodeHeight, barcodeWidth: d.settings.barcodeWidth,
          defaultBrand: d.settings.defaultBrand, defaultPurity: d.settings.defaultPurity,
          defaultProfitMargin: d.settings.defaultProfitMargin,
          showBarcode: d.elements.some(el => el.field === 'barcode'),
          showBarcodeNumber: d.elements.some(el => el.field === 'barcodeNumber'),
          showBrand: d.elements.some(el => el.field === 'brand'),
          showProductInfo: d.elements.some(el => el.field === 'weightPurity'),
          showPurity: d.elements.some(el => ['purity','weightPurity'].includes(el.field)),
          showWeight: d.elements.some(el => ['weight','weightPurity'].includes(el.field)),
          showCategory: d.elements.some(el => el.field === 'category'),
          showHAS: d.elements.some(el => el.field === 'has'),
          showIscilik: d.elements.some(el => el.field === 'iscilik'),
          showLaboredGram: d.elements.some(el => el.field === 'laboredGram'),
          showUSD: d.elements.some(el => el.field === 'usd'),
          showGRAM: d.elements.some(el => el.field === 'gram'),
          showStockCode: d.elements.some(el => el.field === 'stockCode'),
          fontSizeBrand: d.elements.find(el => el.field === 'brand')?.fontSize || 5,
          fontSizeBarcode: d.elements.find(el => el.field === 'barcodeNumber')?.fontSize || 5,
          fontSizeInfo: d.elements.find(el => el.field === 'weightPurity')?.fontSize || 7,
          fontSizeCategory: d.elements.find(el => el.field === 'category')?.fontSize || 7,
          fontSizeStock: d.elements.find(el => el.field === 'stockCode')?.fontSize || 4,
          fontSizeValues: d.elements.find(el => el.field === 'has')?.fontSize || 7,
          leftPadding: 0.5, rightPadding: 0.5, topPadding: 0.3, bottomPadding: 0.3,
          orientation: 'landscape',
        };
        localStorage.setItem('labelPrinterSettings', JSON.stringify(cs));
      }
      toast.success('Etiket dizayni kaydedildi! Tüm kullanıcılar görebilir.');
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('Tasarım kaydedilemedi: ' + error.message);
    }
  };

  const resetDesign = () => {
    if (!window.confirm('Bu taslagi sifirlamak istediginize emin misiniz?')) return;
    setDesigns(prev => {
      const id = _id(prev);
      const d = prev.templates[id];
      const reset = createDefaultDesign(designTypeRef.current, d?.id, d?.name);
      return { ...prev, templates: { ...prev.templates, [id]: reset } };
    });
    setSelectedId(null);
    toast.info('Dizayn varsayılana döndü');
  };

  // ---- Print current design ----
  const printDesign = async () => {
    const d = design;
    const dm = d.dimensions;
    const hasBarcodeEl = elements.some(el => el.field === 'barcode' || el.isBarcode);

    // Qubite Print varsa test etiketi de ham ZPL ile doğrudan yazıcıya gider (kapalıysa önce başlatılır)
    const zebra = await ensureZebraAvailable(null, { onLaunching: () => toast.info('Qubite Print başlatılıyor...') });
    if (zebra.ok) {
      try {
        await printLabelsZebra(d, [zebraTestLabel(d)], { printer: zebra.printer });
        toast.success(`Test etiketi ${zebra.printer.name || 'Zebra'} yazıcısına gönderildi`);
        return;
      } catch (e) {
        toast.warning(`Zebra doğrudan baskı başarısız (${e.message}) — normal yazdırma açılıyor`);
      }
    }

    const elementsHTML = elements.map((el, idx) => {
      const areaOffset = getLabelAreaPrintOffset(el.area, d);

      if (el.field === 'barcode' || el.isBarcode) {
        return `<div class="${LABEL_EL_CLASS}" style="position:absolute; left:${areaOffset + el.x}mm; top:${el.y}mm; width:${el.width}mm; height:${el.height}mm; display:flex; align-items:center; justify-content:${labelAlignJustify(el)}; overflow:hidden;">
          <svg class="barcode-svg" id="barcode-0" style="height:${el.height}mm; width:auto; max-width:100%;"></svg>
        </div>`;
      }

      const fontDef = el.fontFamily ? LABEL_FONTS.find(f => f.id === el.fontFamily) : null;
      const fontFamily = fontDef ? fontDef.css : (['barcodeNumber','stockCode'].includes(el.field) ? "'Courier New', monospace" : "Arial, Helvetica, sans-serif");
      const content = el.content || FIELD_TYPES.find(f => f.id === el.field)?.defaultContent || '';

      return `<div class="${LABEL_EL_CLASS}" style="position:absolute; left:${areaOffset + el.x}mm; top:${el.y}mm; width:${el.width}mm; height:${el.height}mm; display:flex; align-items:center; justify-content:${labelAlignJustify(el)}; overflow:hidden;">
        <span style="font-size:${el.fontSize}pt; font-weight:${el.fontWeight || 'bold'}; font-family:${fontFamily}; color:#000; white-space:nowrap; line-height:1; text-align:${getLabelAlign(el)}; width:100%;">${content}</span>
      </div>`;
    }).join('');

    // Kullanılan Google fontları tespit et
    const usedGoogleFonts = elements.some(el => el.fontFamily && LABEL_FONTS.find(f => f.id === el.fontFamily)?.google);

    const barcodeNum = getSampleBarcode(d);
    const barcodeH = d.settings?.barcodeHeight || 3.5;
    const barcodeW = d.settings?.barcodeWidth || 1.0;

    const labelHtml = `<!DOCTYPE html>
<html><head>
  <title>Etiket Test Çıktısı</title>
  ${usedGoogleFonts ? `<link rel="stylesheet" href="${GOOGLE_FONTS_URL}" />` : ''}
  ${hasBarcodeEl ? '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>' : ''}
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    @page { size: ${dm.labelWidth}mm ${getLabelPageHeightIn(d)}in; margin: 0; }
    ${labelElementCss(d)}
    body { font-family: Arial, sans-serif; background: #f0f0f0; padding: 10mm; }
    .label-container {
      width: ${dm.labelWidth}mm; height: ${dm.labelHeight}mm;
      background: #fff; border: 1px solid #ccc; position: relative; overflow: hidden;
      margin-bottom: 3mm;
    }
    @media print {
      body { background: #fff; padding: 0; }
      .label-container { border: none; margin: 0; page-break-after: always; height: ${getLabelPageHeightIn(d)}in; }
      .no-print { display: none !important; }
    }
  </style>
</head><body>
  <div class="no-print" style="margin-bottom:8mm; font-family:Arial; display:flex; align-items:center; gap:10px;">
    <button onclick="window.print(); if(localStorage.getItem('directPrintEnabled')==='true'){setTimeout(function(){window.close();},500);}" style="padding:8px 20px; background:#2680eb; color:#fff; border:none; border-radius:4px; cursor:pointer; font-size:14px; font-weight:600;">Yazdır</button>
    <span style="color:#666; font-size:13px;">Test çıktısı: ${d.name || 'Taslak'} (${dm.labelWidth}mm x ${dm.labelHeight}mm)</span>
  </div>
  <div class="label-container">${elementsHTML}</div>
  ${hasBarcodeEl ? `<script>
    try {
      JsBarcode("#barcode-0", ${JSON.stringify(barcodeNum)}, ${JSON.stringify(labelBarcodeOptions(barcodeNum, d, barcodeW, barcodeH * 3.78))});
    } catch(e) {}
  <\/script>` : ''}
  <script>
    ${labelShiftScript(getLabelPrintOffset(d))}
    ${labelBarcodeSizeScript(getBarcodeModuleMm(d), barcodeW, d.settings?.printerDpi)}
    applyBarcodeSize();
    applyLabelShift();
    window.onload = function() {
      applyBarcodeSize();
      applyLabelShift();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(applyLabelShift);
      var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
      setTimeout(function() {
        window.print();
        if (directPrint) { setTimeout(function() { window.close(); }, 500); }
      }, 300);
    }
  </script>
</body></html>`;
    handlePrint(labelHtml, 'label');
  };


  // ---- Canvas metrics ----
  const totalW = dim.labelWidth * zoom;
  const totalH = dim.labelHeight * zoom;
  const leftW = dim.leftPrintAreaWidth * zoom;
  const foldW = dim.foldAreaWidth * zoom;
  const infoW = dim.infoPrintAreaWidth * zoom;
  const tailW = dim.tailAreaWidth * zoom;

  // ---- Render element ----
  const renderEl = (el) => {
    const areaX = getAreaOffset(el.area, dim) * zoom;
    const l = areaX + el.x * zoom;
    const t = el.y * zoom;
    const w = el.width * zoom;
    const h = el.height * zoom;
    const sel = selectedId === el.id;
    const dragging = dragInfo?.elId === el.id;
    const fd = FIELD_TYPES.find(f => f.id === el.field);

    const baseStyle = {
      position: 'absolute', left: `${l}px`, top: `${t}px`,
      width: `${w}px`, height: `${h}px`,
      cursor: dragging ? 'grabbing' : 'grab',
      border: sel ? `2px solid ${T.accent}` : '1px dashed rgba(0,0,0,0.08)',
      borderRadius: '1px',
      background: sel ? 'rgba(38,128,235,0.08)' : 'transparent',
      overflow: 'hidden', zIndex: sel ? 10 : 1,
      transition: dragging ? 'none' : 'border 0.15s',
      userSelect: 'none',
    };

    if (el.isBarcode || el.field === 'barcode') {
      return (
        <div key={el.id} onMouseDown={(e) => handleMouseDown(e, el.id)} style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: labelAlignJustify(el) }}>
          <BarcodePreview value={getSampleBarcode(design)} design={design} zoom={zoom} />
          {sel && <div style={elBadge}>{fd?.label || 'Barkod'}</div>}
        </div>
      );
    }

    return (
      <div key={el.id} onMouseDown={(e) => handleMouseDown(e, el.id)} style={{
        ...baseStyle, display: 'flex', alignItems: 'center', padding: '0 2px',
        justifyContent: el.textAlign === 'left' ? 'flex-start' : el.textAlign === 'right' ? 'flex-end' : 'center',
      }}>
        <span style={{
          fontSize: `${el.fontSize * 25.4 / 72 * zoom}px`,
          fontWeight: el.fontWeight || 'bold',
          fontFamily: (el.fontFamily && LABEL_FONTS.find(f => f.id === el.fontFamily)?.css) || (['barcodeNumber','stockCode'].includes(el.field) ? "'Courier New', monospace" : "Arial, sans-serif"),
          color: '#000', whiteSpace: 'nowrap', lineHeight: 1,
          textAlign: el.textAlign || 'center', width: '100%',
        }}>
          {el.content || fd?.defaultContent || ''}
        </span>
        {sel && <div style={elBadge}>{fd?.label || el.field}</div>}
      </div>
    );
  };

  const elBadge = {
    position: 'absolute', top: '-16px', left: '0',
    fontSize: '9px', color: '#fff', fontWeight: '600',
    whiteSpace: 'nowrap', background: T.accent,
    padding: '1px 5px', borderRadius: '2px', letterSpacing: '0.3px',
  };

  const inputStyle = {
    width: '100%', padding: '6px 8px', background: T.inputBg,
    border: `1px solid ${T.inputBorder}`, borderRadius: '4px',
    color: T.text, fontSize: '12px', outline: 'none',
  };
  const labelStyle = { fontSize: '10px', color: T.textSec, display: 'block', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, display: 'flex', flexDirection: 'column' }}>
      {/* ===== HEADER ===== */}
      <div style={{
        background: T.headerBg, borderBottom: `1px solid ${T.headerBorder}`,
        padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '12px', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate('/settings')} style={headerBtn}>
            <ArrowLeft size={15} />
          </button>
          <div style={{ borderLeft: `1px solid ${T.border}`, paddingLeft: '12px' }}>
            <h1 style={{ fontSize: '15px', margin: 0, color: T.text, fontWeight: '600', letterSpacing: '0.3px' }}>
              Etiket Dizaynı
            </h1>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Type selector - admin hepsini görür, kasiyerler sadece kendi metal tipini */}
          <div style={{ display: 'flex', background: T.panelDark, borderRadius: '4px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            {[{ type: 'gold', label: 'Altın', color: T.gold }, { type: 'silver', label: 'Gümüş', color: T.silver }]
              .filter(t => isAdmin || t.type === storeMetalType)
              .map(t => (
              <button key={t.type}
                onClick={() => { setDesignType(t.type); setSelectedId(null); setShowTemplates(false); }}
                style={{
                  padding: '6px 14px', border: 'none', fontSize: '12px', fontWeight: '600',
                  background: designType === t.type ? t.color : 'transparent',
                  color: designType === t.type ? '#000' : T.textDim,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Templates dropdown */}
          <div ref={templateDropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              style={{
                ...headerBtn,
                background: showTemplates ? T.accent : 'transparent',
                color: showTemplates ? '#fff' : '#ccc',
                borderColor: showTemplates ? T.accent : 'rgba(255,255,255,0.1)',
                gap: '5px', fontSize: '12px', padding: '6px 12px',
              }}>
              <FileText size={14} />
              <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {design.name || 'Taslak'}
              </span>
              <ChevronDown size={12} />
            </button>

            {showTemplates && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                background: T.panel, border: `1px solid ${T.border}`, borderRadius: '6px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.5)', zIndex: 1000,
                minWidth: '280px', maxHeight: '400px', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}>
                {/* Dropdown header */}
                <div style={{
                  padding: '10px 12px', borderBottom: `1px solid ${T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {designType === 'gold' ? 'Altın' : 'Gümüş'} Taslakları
                  </span>
                  <span style={{ fontSize: '10px', color: T.textDim }}>{typeTemplates.length} taslak</span>
                </div>

                {/* Template list */}
                <div style={{ flex: 1, overflowY: 'auto', maxHeight: '260px' }}>
                  {typeTemplates.map(tmpl => {
                    const isActive = tmpl.id === designId;
                    const isDefault = tmpl.id === 'gold-default' || tmpl.id === 'silver-default';
                    const isRenaming = renamingId === tmpl.id;

                    return (
                      <div key={tmpl.id}
                        style={{
                          padding: '8px 12px',
                          background: isActive ? T.accentBg : 'transparent',
                          borderBottom: `1px solid ${T.border}`,
                          display: 'flex', alignItems: 'center', gap: '8px',
                          cursor: 'pointer', transition: 'background 0.1s',
                        }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = T.panelLight; }}
                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                        onClick={() => { if (!isRenaming) switchTemplate(tmpl.id); }}
                      >
                        {/* Active indicator */}
                        <div style={{
                          width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                          background: isActive ? T.accent : 'transparent',
                          border: isActive ? 'none' : `1px solid ${T.textDim}`,
                        }} />

                        {/* Name */}
                        {isRenaming ? (
                          <div style={{ flex: 1, display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text" value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenamingId(null); }}
                              autoFocus
                              style={{ ...inputStyle, flex: 1, padding: '3px 6px', fontSize: '12px' }}
                            />
                            <button onClick={confirmRename} style={{ ...iconBtnSmall, color: '#4ade80' }}><Check size={13} /></button>
                            <button onClick={() => setRenamingId(null)} style={{ ...iconBtnSmall, color: T.danger }}><X size={13} /></button>
                          </div>
                        ) : (
                          <>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12px', color: isActive ? T.accent : T.text, fontWeight: isActive ? '600' : '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {tmpl.name || tmpl.id}
                              </div>
                              <div style={{ fontSize: '9px', color: T.textDim, marginTop: '1px' }}>
                                {tmpl.elements?.length || 0} eleman
                              </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => startRename(tmpl.id)} style={iconBtnSmall} title="Yeniden adlandir">
                                <Edit3 size={12} />
                              </button>
                              {!isDefault && (
                                <button onClick={() => deleteTemplate(tmpl.id)} style={{ ...iconBtnSmall, color: T.danger }} title="Sil">
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Dropdown footer - create actions */}
                <div style={{
                  padding: '8px', borderTop: `1px solid ${T.border}`,
                  display: 'flex', gap: '4px',
                }}>
                  <button onClick={createNewTemplate}
                    style={{
                      flex: 1, padding: '7px 10px', background: T.panelDark, border: `1px solid ${T.border}`,
                      borderRadius: '4px', color: T.textSec, cursor: 'pointer', fontSize: '11px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSec; }}
                  >
                    <Plus size={13} /> Yeni Taslak
                  </button>
                  <button onClick={duplicateTemplate}
                    style={{
                      flex: 1, padding: '7px 10px', background: T.panelDark, border: `1px solid ${T.border}`,
                      borderRadius: '4px', color: T.textSec, cursor: 'pointer', fontSize: '11px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSec; }}
                  >
                    <Copy size={13} /> Kopyala
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ width: '1px', height: '24px', background: T.border, margin: '0 4px' }} />

          {/* Zoom */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', background: T.panelDark, borderRadius: '4px', border: `1px solid ${T.border}`, padding: '2px 6px' }}>
            <button onClick={() => setZoom(z => Math.max(2, z - 1))} style={iconBtn}><ZoomOut size={14} /></button>
            <span style={{ fontSize: '11px', minWidth: '30px', textAlign: 'center', color: T.textSec }}>{zoom}x</span>
            <button onClick={() => setZoom(z => Math.min(50, z + 1))} style={iconBtn}><ZoomIn size={14} /></button>
          </div>

          <div style={{ width: '1px', height: '24px', background: T.border, margin: '0 4px' }} />

          <button onClick={resetDesign} style={{ ...headerBtn, color: T.textDim, gap: '4px', fontSize: '11px' }}>
            <RotateCcw size={13} /> Sifirla
          </button>
          <button onClick={printDesign} style={{ ...headerBtn, color: T.text, gap: '4px', fontSize: '11px', borderColor: 'rgba(255,255,255,0.2)' }}>
            <Printer size={13} /> Yazdır
          </button>
          <button onClick={saveDesigns} style={{
            background: T.accent, border: 'none', color: '#fff',
            padding: '6px 16px', borderRadius: '4px', cursor: 'pointer',
            fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px',
          }}>
            <Save size={14} /> Kaydet
          </button>
        </div>
      </div>

      {/* ===== MAIN LAYOUT ===== */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* LEFT SIDEBAR - Fields */}
        <div style={{ width: '260px', minWidth: '260px', background: T.panel, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}`, fontSize: '11px', fontWeight: '600', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Layers size={13} /> Alanlar
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            <p style={{ fontSize: '10px', color: T.textDim, margin: '0 0 10px 0', lineHeight: 1.5, padding: '0 4px' }}>
              Alanlari surukleyip etiketin uzerine birakin
            </p>
            {FIELD_TYPES.map(field => {
              const FIcon = field.Icon;
              return (
                <div key={field.id} draggable onDragStart={(e) => onSidebarDragStart(e, field.id)}
                  style={{
                    padding: '9px 10px', background: 'transparent',
                    border: '1px solid transparent', borderRadius: '5px', marginBottom: '3px',
                    cursor: 'grab', display: 'flex', alignItems: 'center', gap: '10px',
                    transition: 'all 0.1s', userSelect: 'none',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.panelLight; e.currentTarget.style.borderColor = T.borderLight; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  <Grip size={13} style={{ color: T.textDim, flexShrink: 0 }} />
                  <FIcon size={15} style={{ color: T.accent, flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: T.text, fontSize: '12px', fontWeight: '500' }}>{field.label}</div>
                    <div style={{ color: T.textDim, fontSize: '9px', marginTop: '1px' }}>
                      {field.isBarcode ? 'Barkod goruntus' : field.defaultContent}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CENTER - Canvas */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: T.canvas,
            backgroundImage: `radial-gradient(circle, ${T.canvasDot} 1px, transparent 1px)`,
            backgroundSize: '20px 20px', overflow: 'auto', padding: '40px', position: 'relative',
          }}
          onClick={(e) => { if (e.target === e.currentTarget || e.target.classList.contains('label-area-bg')) setSelectedId(null); }}
          >
            <div ref={canvasRef} style={{
              width: `${totalW}px`, height: `${totalH}px`,
              position: 'relative', display: 'flex', flexDirection: 'row',
              boxShadow: '0 2px 20px rgba(0,0,0,0.6)', borderRadius: '2px',
              overflow: 'visible', flexShrink: 0,
            }}>
              {/* Left Print */}
              <div className="label-area-bg" onDragOver={onAreaDragOver} onDrop={(e) => onAreaDrop(e, 'left')}
                style={{ width: `${leftW}px`, height: `${totalH}px`, background: '#fff', borderRight: '1px dashed #d0d0d0', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-20px', left: 0, right: 0, textAlign: 'center', fontSize: '9px', color: T.accent, fontWeight: '600', whiteSpace: 'nowrap' }}>
                  Sol Baski ({dim.leftPrintAreaWidth}mm)
                </div>
              </div>
              {/* Fold */}
              <div className="label-area-bg" onDragOver={onAreaDragOver} onDrop={(e) => onAreaDrop(e, 'fold')}
                style={{ width: `${foldW}px`, height: `${totalH}px`, background: '#f0ece0', borderRight: '1px dashed #d0d0d0', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-20px', left: 0, right: 0, textAlign: 'center', fontSize: '8px', color: T.gold, fontWeight: '600', whiteSpace: 'nowrap' }}>
                  Kat
                </div>
              </div>
              {/* Info Print */}
              <div className="label-area-bg" onDragOver={onAreaDragOver} onDrop={(e) => onAreaDrop(e, 'info')}
                style={{ width: `${infoW}px`, height: `${totalH}px`, background: '#fff', borderRight: '1px dashed #d0d0d0', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-20px', left: 0, right: 0, textAlign: 'center', fontSize: '9px', color: '#5cb85c', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  Bilgi ({dim.infoPrintAreaWidth}mm)
                </div>
              </div>
              {/* Tail */}
              <div className="label-area-bg" onDragOver={onAreaDragOver} onDrop={(e) => onAreaDrop(e, 'tail')}
                style={{ width: `${tailW}px`, height: `${totalH}px`, background: '#f5f5f5', position: 'relative', borderRadius: '0 50px 50px 0' }}>
                <div style={{ position: 'absolute', top: '-20px', left: 0, right: 0, textAlign: 'center', fontSize: '9px', color: T.textDim, fontWeight: '600', whiteSpace: 'nowrap' }}>
                  Kuyruk ({dim.tailAreaWidth}mm)
                </div>
              </div>

              {elements.map(el => renderEl(el))}

              <div style={{ position: 'absolute', bottom: '-22px', left: 0, right: 0, textAlign: 'center', fontSize: '10px', color: T.textDim }}>
                {dim.labelWidth}mm x {dim.labelHeight}mm
              </div>
            </div>
          </div>

          {/* Bottom settings panel */}
          <div style={{ background: T.panel, borderTop: `1px solid ${T.border}` }}>
            <button onClick={() => setShowDimensions(!showDimensions)}
              style={{
                width: '100%', background: 'none', border: 'none',
                borderBottom: showDimensions ? `1px solid ${T.border}` : 'none',
                color: T.textSec, padding: '7px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Settings size={13} /> Boyutlar & Yazici
              </span>
              {showDimensions ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {showDimensions && (
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'labelWidth', label: 'Toplam Uzunluk' },
                    { key: 'labelHeight', label: 'Kalinlik' },
                    { key: 'leftPrintAreaWidth', label: 'Sol Baski' },
                    { key: 'foldAreaWidth', label: 'Katlama' },
                    { key: 'infoPrintAreaWidth', label: 'Bilgi Alani' },
                    { key: 'tailAreaWidth', label: 'Kuyruk' },
                  ].map(item => (
                    <div key={item.key} style={{ minWidth: '100px' }}>
                      <label style={labelStyle}>{item.label} (mm)</label>
                      <input type="number" step="0.5" value={dim[item.key]}
                        onChange={(e) => _update(d => ({ ...d, dimensions: { ...d.dimensions, [item.key]: parseFloat(e.target.value) || 0 } }))}
                        style={inputStyle} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { type: 'select', key: 'printerModel', label: 'Yazici', w: '130px', opts: ['Zebra ZD220','Zebra ZD421','Zebra GK420t','TSC TTP-244','Godex G500','Other'] },
                    { type: 'select', key: 'printerDpi', label: 'DPI', w: '90px', opts: [203, 300], isNum: true },
                    { type: 'number', key: 'barcodeHeight', label: 'Barkod Yuk. (mm)', w: '90px', step: 0.5 },
                    { type: 'number', key: 'barcodeWidth', label: 'Barkod Kal.', w: '80px', step: 0.1 },
                    { type: 'text', key: 'defaultBrand', label: 'Varsayilan Marka', w: '130px' },
                    { type: 'text', key: 'defaultPurity', label: 'Varsayilan Ayar', w: '70px' },
                    { type: 'number', key: 'defaultProfitMargin', label: 'Kar Orani', w: '70px', step: 0.1 },
                  ].map(item => (
                    <div key={item.key} style={{ minWidth: item.w }}>
                      <label style={labelStyle}>{item.label}</label>
                      {item.type === 'select' ? (
                        <select value={settings[item.key]}
                          onChange={(e) => _update(d => ({ ...d, settings: { ...d.settings, [item.key]: item.isNum ? parseInt(e.target.value) : e.target.value } }))}
                          style={{ ...inputStyle, cursor: 'pointer' }}>
                          {item.opts.map(o => <option key={o} value={o}>{item.isNum ? `${o} DPI` : (o === 'Other' ? 'Diger' : o)}</option>)}
                        </select>
                      ) : (
                        <input type={item.type} step={item.step}
                          value={settings[item.key] || ''}
                          onChange={(e) => _update(d => ({ ...d, settings: { ...d.settings, [item.key]: item.type === 'number' ? parseFloat(e.target.value) : e.target.value } }))}
                          style={inputStyle} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ minWidth: '150px' }}>
                    <label style={labelStyle}>Baskı Kaydırma (mm)</label>
                    <input type="number" step="0.25" value={settings.printOffsetX ?? 0}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        const val = Number.isFinite(v) ? v : 0;
                        setDesigns(prev => {
                          const templates = {};
                          Object.entries(prev.templates).forEach(([k, t]) => {
                            templates[k] = { ...t, settings: { ...(t.settings || {}), printOffsetX: val } };
                          });
                          return { ...prev, templates };
                        });
                      }}
                      style={inputStyle} />
                  </div>
                  {[
                    { key: 'printOffsetY', label: 'Dikey Kaydırma (mm)', title: 'Tüm etiketi aşağı (+) / yukarı (−) kaydırır' },
                    { key: 'labelPageAdjustMm', label: 'Etiket Aralığı (mm)', title: 'Toplu baskıda etiketler gittikçe yukarı kayıyorsa artırın (+0.25 adımlarla), aşağı kayıyorsa azaltın' },
                  ].map(item => (
                    <div key={item.key} style={{ minWidth: '150px' }} title={item.title}>
                      <label style={labelStyle}>{item.label}</label>
                      <input type="number" step="0.25" value={settings[item.key] ?? 0}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          const val = Number.isFinite(v) ? v : 0;
                          setDesigns(prev => {
                            const templates = {};
                            Object.entries(prev.templates).forEach(([k, t]) => {
                              templates[k] = { ...t, settings: { ...(t.settings || {}), [item.key]: val } };
                            });
                            return { ...prev, templates };
                          });
                        }}
                        style={inputStyle} />
                      {item.key === 'labelPageAdjustMm' && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                          Sayfa boyu: {(parseFloat(getLabelPageHeightIn(design)) * 25.4).toFixed(2)} mm
                        </div>
                      )}
                    </div>
                  ))}
                  <div style={{ minWidth: '150px' }}>
                    <label style={labelStyle}>Barkod Çizgi (mm)</label>
                    <input type="number" step="0.01" min="0.1" value={settings.barcodeModuleMm ?? DEFAULT_BARCODE_MODULE_MM}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!Number.isFinite(v) || v <= 0) return;
                        setDesigns(prev => {
                          const templates = {};
                          Object.entries(prev.templates).forEach(([k, t]) => {
                            templates[k] = { ...t, settings: { ...(t.settings || {}), barcodeModuleMm: v } };
                          });
                          return { ...prev, templates };
                        });
                      }}
                      style={inputStyle} />
                  </div>
                  <span style={{ fontSize: '10px', color: T.textDim, lineHeight: 1.4, flex: 1, minWidth: '180px', paddingBottom: '6px' }}>
                    Kaydırma: baskıda tüm öğeleri kaydırır (eksi = sola). Barkod çizgi: barkod esnetilmeden bu kalınlıkta basılır, kutusundan büyükse kutuya sığacak kadar daraltılır; kutu içindeki yeri öğenin Barkod Hizası ile seçilir. Tüm barkodlar Code128 (düz çizgi, aynı yükseklik) basılır. Hepsi tüm taslaklara uygulanır. Kaydetmeyi unutmayın.
                  </span>
                </div>
                <ZebraDirectPrintPanel design={design} labelStyle={labelStyle} inputStyle={inputStyle} />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR - Properties */}
        <div style={{ width: '250px', minWidth: '250px', background: T.panel, borderLeft: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontSize: '11px', fontWeight: '600', color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Settings size={13} /> Özellikler
          </div>

          {selected ? (
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
              {/* Element info badge */}
              <div style={{ padding: '8px 10px', background: T.accentBg, borderRadius: '4px', marginBottom: '12px', border: `1px solid ${T.accentBorder}` }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: T.accent }}>
                  {FIELD_TYPES.find(f => f.id === selected.field)?.label || selected.field}
                </div>
                <div style={{ fontSize: '10px', color: T.textDim, marginTop: '2px' }}>
                  {AREA_LABELS[selected.area] || selected.area}
                </div>
              </div>

              {/* Content editing - for ALL text elements */}
              {selected.field !== 'barcode' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>
                    <Type size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                    Icerik / Onizleme Metni
                  </label>
                  <input
                    type="text"
                    value={selected.content || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const elId = selected.id;
                      // Direct state mutation with deep clone - zero closure dependency
                      setDesigns(prev => {
                        const clone = JSON.parse(JSON.stringify(prev));
                        const t = designTypeRef.current;
                        const id = t === 'gold' ? (clone.activeGoldId || 'gold-default') : (clone.activeSilverId || 'silver-default');
                        const tmpl = clone.templates[id];
                        if (!tmpl) return prev;
                        const el = tmpl.elements.find(e => e.id === elId);
                        if (!el) return prev;
                        el.content = val;
                        return clone;
                      });
                    }}
                    placeholder={FIELD_TYPES.find(f => f.id === selected.field)?.defaultContent || ''}
                    style={{ ...inputStyle, fontSize: '13px', padding: '8px' }}
                  />
                  {!['brand', 'customText'].includes(selected.field) && (
                    <span style={{ fontSize: '9px', color: T.textDim, marginTop: '3px', display: 'block' }}>
                      {selected.field === 'price' ? 'Fiyatın önüne/arkasına metin ekleyebilirsiniz (örn: "İndirimli 1.250 ₺")' : 'Yazdırılırken ürün bilgisi otomatik dolar'}
                    </span>
                  )}
                </div>
              )}

              {/* Currency symbol selector for price field */}
              {selected.field === 'price' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>
                    <DollarSign size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                    Para Birimi
                  </label>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        const elId = selected.id;
                        setDesigns(prev => {
                          const clone = JSON.parse(JSON.stringify(prev));
                          const t = designTypeRef.current;
                          const id = t === 'gold' ? (clone.activeGoldId || 'gold-default') : (clone.activeSilverId || 'silver-default');
                          const tmpl = clone.templates[id];
                          if (!tmpl) return prev;
                          const el = tmpl.elements.find(e => e.id === elId);
                          if (!el) return prev;
                          el.currencySymbol = '';
                          el.content = el.showCurrency === false ? '1.250' : '1.250 ₺';
                          return clone;
                        });
                      }}
                      style={{
                        flex: 1, padding: '6px 4px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '600',
                        background: !selected.currencySymbol ? T.accent : T.panelDark,
                        border: `1px solid ${!selected.currencySymbol ? T.accent : T.border}`,
                        color: !selected.currencySymbol ? '#fff' : T.textSec,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      Oto
                    </button>
                    {CURRENCY_SYMBOLS.map(c => (
                      <button key={c.id}
                        onClick={() => {
                          const elId = selected.id;
                          setDesigns(prev => {
                            const clone = JSON.parse(JSON.stringify(prev));
                            const t = designTypeRef.current;
                            const id = t === 'gold' ? (clone.activeGoldId || 'gold-default') : (clone.activeSilverId || 'silver-default');
                            const tmpl = clone.templates[id];
                            if (!tmpl) return prev;
                            const el = tmpl.elements.find(e => e.id === elId);
                            if (!el) return prev;
                            el.currencySymbol = c.symbol;
                            el.content = el.showCurrency === false ? '1.250' : `1.250 ${c.symbol}`;
                            return clone;
                          });
                        }}
                        style={{
                          flex: 1, padding: '6px 4px', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                          background: selected.currencySymbol === c.symbol ? T.accent : T.panelDark,
                          border: `1px solid ${selected.currencySymbol === c.symbol ? T.accent : T.border}`,
                          color: selected.currencySymbol === c.symbol ? '#fff' : T.textSec,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
                        }}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <span style={{ fontSize: '9px', color: T.textDim, marginTop: '3px', display: 'block' }}>
                    {!selected.currencySymbol ? 'Ürünün kendi para birimi kullanılır' : 'Seçilen birime çevrilerek yazdırılır'}
                  </span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer', fontSize: '11px', color: T.text, fontWeight: '500' }}>
                    <input
                      type="checkbox"
                      checked={selected.showCurrency !== false}
                      onChange={(e) => {
                        const elId = selected.id;
                        const checked = e.target.checked;
                        setDesigns(prev => {
                          const clone = JSON.parse(JSON.stringify(prev));
                          const t = designTypeRef.current;
                          const id = t === 'gold' ? (clone.activeGoldId || 'gold-default') : (clone.activeSilverId || 'silver-default');
                          const tmpl = clone.templates[id];
                          if (!tmpl) return prev;
                          const el = tmpl.elements.find(e => e.id === elId);
                          if (!el) return prev;
                          el.showCurrency = checked;
                          const sym = el.currencySymbol || '₺';
                          el.content = checked ? `1.250 ${sym}` : '1.250';
                          return clone;
                        });
                      }}
                      style={{ width: '14px', height: '14px', accentColor: T.accent, cursor: 'pointer' }}
                    />
                    Para birimini etikette göster
                  </label>
                </div>
              )}

              {/* Position */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>
                  <Move size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                  Pozisyon (mm)
                </label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {['x', 'y'].map(axis => (
                    <div key={axis} style={{ flex: 1 }}>
                      <label style={{ fontSize: '9px', color: T.textDim, display: 'block', marginBottom: '2px' }}>{axis.toUpperCase()}</label>
                      <input type="number" step="0.25" value={selected[axis]}
                        onChange={(e) => updateEl(selected.id, { [axis]: parseFloat(e.target.value) || 0 })}
                        style={inputStyle} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Boyut (mm)</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[{ k: 'width', l: 'Gen.' }, { k: 'height', l: 'Yuk.' }].map(s => (
                    <div key={s.k} style={{ flex: 1 }}>
                      <label style={{ fontSize: '9px', color: T.textDim, display: 'block', marginBottom: '2px' }}>{s.l}</label>
                      <input type="number" step="0.25" value={selected[s.k]}
                        onChange={(e) => updateEl(selected.id, { [s.k]: parseFloat(e.target.value) || 0.5 })}
                        style={inputStyle} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Barkod hizası + kutuya sığma bilgisi */}
              {(selected.isBarcode || selected.field === 'barcode') && (() => {
                const moduleMm = getBarcodeModuleMm(design);
                const samples = [
                  { label: 'Örnek', value: getSampleBarcode(design) },
                  { label: '8 hane', value: '30193468' },
                  { label: '10 hane', value: '2731234567' },
                ];
                return (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={labelStyle}>Barkod Hizası (kutu içinde)</label>
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                      {[
                        { v: 'left', icon: <AlignLeft size={12} />, t: 'Sola' },
                        { v: 'center', icon: <AlignCenter size={12} />, t: 'Ortala' },
                        { v: 'right', icon: <AlignRight size={12} />, t: 'Sağa' },
                      ].map(a => {
                        const on = getLabelAlign(selected) === a.v;
                        return (
                          <button key={a.v} onClick={() => updateEl(selected.id, { textAlign: a.v })}
                            style={{
                              flex: 1, padding: '5px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px',
                              background: on ? T.accent : T.panelDark,
                              border: `1px solid ${on ? T.accent : T.border}`,
                              color: on ? '#fff' : T.textSec,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                            }}>
                            {a.icon} {a.t}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '10px', color: T.textDim, lineHeight: 1.6 }}>
                      {samples.map(smp => {
                        const w = barcodeModules(smp.value, design) * moduleMm;
                        const fits = w <= selected.width + 1e-6;
                        const fmt = getLabelBarcodeFormat(smp.value, design);
                        return (
                          <div key={smp.label} style={{ color: fits ? T.textDim : '#e8a33d' }}>
                            {smp.label} ({fmt === 'CODE128' ? 'Code128' : fmt.replace('EAN', 'EAN-')}): {w.toFixed(1)} mm
                            {fits ? '' : ' — kutuya sığmaz, daraltılarak basılır'}
                          </div>
                        );
                      })}
                      <div>Kutu genişliği: {selected.width} mm. Barkod kutunun dışına taşmaz.</div>
                    </div>
                  </div>
                );
              })()}

              {/* Font controls */}
              {!selected.isBarcode && selected.field !== 'barcode' && (
                <>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={labelStyle}>Yazi Tipi</label>
                    <select
                      value={selected.fontFamily || 'Arial'}
                      onChange={(e) => updateEl(selected.id, { fontFamily: e.target.value })}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      {LABEL_FONTS.map(f => (
                        <option key={f.id} value={f.id} style={{ fontFamily: f.css }}>{f.label}</option>
                      ))}
                    </select>
                    <div style={{
                      marginTop: '4px', padding: '5px 8px', background: '#fff', borderRadius: '3px',
                      fontFamily: (LABEL_FONTS.find(f => f.id === (selected.fontFamily || 'Arial'))?.css) || 'Arial, sans-serif',
                      fontSize: '11px', fontWeight: selected.fontWeight || 'bold', color: '#000', textAlign: 'center',
                    }}>
                      Akik 14K 3.50gr ÇĞİÖŞÜ
                    </div>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={labelStyle}>Font Boyutu</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="range" min="1" max="50" step="0.5" value={Math.min(selected.fontSize, 50)}
                        onChange={(e) => updateEl(selected.id, { fontSize: parseFloat(e.target.value) })}
                        style={{ flex: 1, accentColor: T.accent }} />
                      <input type="number" step="0.5" min="0.5" value={selected.fontSize}
                        onChange={(e) => updateEl(selected.id, { fontSize: parseFloat(e.target.value) || 0.5 })}
                        style={{ width: '52px', padding: '4px 6px', background: T.inputBg, border: `1px solid ${T.inputBorder}`, borderRadius: '4px', color: T.text, fontSize: '11px', textAlign: 'right' }} />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                    <button onClick={() => updateEl(selected.id, { fontWeight: selected.fontWeight === 'bold' ? 'normal' : 'bold' })}
                      style={{
                        flex: 1, padding: '5px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px',
                        background: selected.fontWeight === 'bold' ? T.accent : T.panelDark,
                        border: `1px solid ${selected.fontWeight === 'bold' ? T.accent : T.border}`,
                        color: selected.fontWeight === 'bold' ? '#fff' : T.textSec,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                      }}>
                      <Bold size={12} /> Kalin
                    </button>
                    {[
                      { v: 'left', icon: <AlignLeft size={12} /> },
                      { v: 'center', icon: <AlignCenter size={12} /> },
                      { v: 'right', icon: <AlignRight size={12} /> },
                    ].map(a => (
                      <button key={a.v} onClick={() => updateEl(selected.id, { textAlign: a.v })}
                        style={{
                          padding: '5px 8px', borderRadius: '3px', cursor: 'pointer',
                          background: selected.textAlign === a.v ? T.accent : T.panelDark,
                          border: `1px solid ${selected.textAlign === a.v ? T.accent : T.border}`,
                          color: selected.textAlign === a.v ? '#fff' : T.textSec,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        {a.icon}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Move to area */}
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Baski Alani</label>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {AREA_KEYS.map(area => (
                    <button key={area}
                      onClick={() => updateEl(selected.id, { area, width: dim[AREA_DIM_KEY[area]], x: 0 })}
                      style={{
                        flex: '1 1 45%', padding: '5px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px',
                        background: selected.area === area ? T.accent : T.panelDark,
                        border: `1px solid ${selected.area === area ? T.accent : T.border}`,
                        color: selected.area === area ? '#fff' : T.textSec,
                      }}>
                      {AREA_LABELS[area]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '4px', marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${T.border}` }}>
                <button onClick={() => duplicateEl(selected.id)}
                  style={{ flex: 1, padding: '6px', background: T.panelDark, border: `1px solid ${T.border}`, borderRadius: '3px', color: T.textSec, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '11px' }}>
                  <Copy size={12} /> Kopyala
                </button>
                <button onClick={() => removeEl(selected.id)}
                  style={{ flex: 1, padding: '6px', background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, borderRadius: '3px', color: T.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '11px' }}>
                  <Trash2 size={12} /> Sil
                </button>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', color: T.textDim, textAlign: 'center' }}>
              <Move size={28} style={{ marginBottom: '10px', opacity: 0.4 }} />
              <p style={{ fontSize: '12px', lineHeight: 1.5 }}>Eleman secin veya<br />sol panelden surukleyin</p>
            </div>
          )}

          {/* Elements list */}
          <div style={{ borderTop: `1px solid ${T.border}`, maxHeight: '200px', overflowY: 'auto' }}>
            <div style={{ padding: '7px 12px', fontSize: '10px', fontWeight: '600', color: T.textDim, background: T.panelDark, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              Elemanlar ({elements.length})
            </div>
            {elements.map(el => {
              const fd = FIELD_TYPES.find(f => f.id === el.field);
              const FIcon = fd?.Icon || Tag;
              return (
                <div key={el.id} onClick={() => setSelectedId(el.id)}
                  style={{
                    padding: '6px 12px',
                    background: selectedId === el.id ? T.accentBg : 'transparent',
                    borderBottom: `1px solid ${T.border}`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: '11px', transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { if (selectedId !== el.id) e.currentTarget.style.background = T.panelLight; }}
                  onMouseLeave={(e) => { if (selectedId !== el.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: T.text }}>
                    <FIcon size={12} style={{ color: T.accent }} />
                    {fd?.label || el.field}
                  </span>
                  <span style={{
                    fontSize: '9px', color: el.area === 'left' ? T.accent : '#5cb85c',
                    background: el.area === 'left' ? T.accentBg : 'rgba(92,184,92,0.12)',
                    padding: '1px 5px', borderRadius: '2px',
                  }}>
                    {AREA_LABELS[el.area] || el.area}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Shared styles
const headerBtn = {
  background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
  color: '#ccc', padding: '6px 8px', borderRadius: '4px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px',
};
const iconBtn = {
  background: 'none', border: 'none', color: '#999', cursor: 'pointer',
  padding: '3px', display: 'flex', alignItems: 'center',
};
const iconBtnSmall = {
  background: 'none', border: 'none', color: '#777', cursor: 'pointer',
  padding: '3px', display: 'flex', alignItems: 'center', borderRadius: '3px',
};

export default LabelDesign;

// Önizleme ve test baskısında kullanılan barkod: tasarımdaki "Barkod No" öğesinin örnek içeriği
const getSampleBarcode = (d) => {
  const v = String((d?.elements || []).find(el => el.field === 'barcodeNumber')?.content || '').trim();
  // Eski varsayılan 01010163 geçerli EAN-8 değil (Code128 basılırdı) → aynı numaranın geçerli EAN-8'i
  if (v === '01010163' || !/^[\x20-\x7E]+$/.test(v)) return '01010169';
  return v;
};

// Barkodun modül (en ince çizgi birimi) sayısı → genişlik = modül × Barkod Çizgi (mm)
const barcodeModules = (value, d) => {
  try {
    const c = document.createElement('canvas');
    JsBarcode(c, String(value), labelBarcodeOptions(value, d, 1, 10));
    return c.width;
  } catch {
    return 0;
  }
};

// Tasarım alanında gerçek barkod (baskıdaki genişlik ve formatla); kutudan büyükse kutuya sığdırılır
function BarcodePreview({ value, design, zoom }) {
  const ref = useRef(null);
  const [modules, setModules] = useState(0);
  const moduleMm = getBarcodeModuleMm(design);
  const format = getLabelBarcodeFormat(value, design);
  useEffect(() => {
    if (!ref.current) return;
    try {
      JsBarcode(ref.current, String(value), labelBarcodeOptions(value, design, 1, 40));
      setModules(parseFloat(ref.current.getAttribute('width')) || 0);
    } catch {
      setModules(0);
    }
  }, [value, format, design]);
  return (
    <svg ref={ref} preserveAspectRatio="none"
      style={{ height: '100%', width: modules ? `${modules * moduleMm * zoom}px` : 'auto', maxWidth: '100%', flexShrink: 0, display: 'block' }} />
  );
}

// Tasarımdaki örnek içeriklerle test etiketi (Zebra doğrudan baskı için)
const zebraTestLabel = (d) => ({
  qty: 1,
  barcodeValue: getSampleBarcode(d),
  getContent: (el) => el.content || FIELD_TYPES.find(f => f.id === el.field)?.defaultContent || '',
});

// Bu bilgisayardaki Zebra yazıcı: Qubite Print durumu, yazıcı seçimi, kalibrasyon ve
// bu yazıcıya özel ince ayar (ayarlar tarayıcıda tutulur — her mağaza bilgisayarı kendi yazıcısı için).
function ZebraDirectPrintPanel({ design, labelStyle, inputStyle }) {
  const [status, setStatus] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [settings, setSettings] = useState(getZebraSettings);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setStatus(null);
    const r = await checkZebraAvailable();
    setStatus(r);
    if (r.ok || r.reason === 'no-printer') {
      try { setPrinters(await listZebraPrinters()); } catch { setPrinters([]); }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    try {
      // Önizlemede bu yazıcının model + ince ayar kaydırması da gösterilir (yazıcıda komutla uygulanır)
      const canvas = renderLabelCanvas(design, zebraTestLabel(design), getDeviceShiftMm());
      setPreview(canvas.toDataURL('image/png'));
    } catch { setPreview(null); }
  }, [design, settings.offsetXmm, settings.offsetYmm, settings.model]);

  const update = (patch) => setSettings(saveZebraSettings(patch));

  const calibrate = async () => {
    if (!window.confirm('Yazıcı kalibre edilecek: birkaç boş etiket ilerletilecek. Devam edilsin mi?')) return;
    setBusy(true);
    try {
      const printer = await getZebraPrinter();
      if (!printer) throw new Error('yazıcı bulunamadı');
      await sendZpl(printer, buildCalibrationZpl(design));
      toast.success('Kalibrasyon gönderildi. Yazıcı durunca test etiketi basın.');
    } catch (e) {
      toast.error(`Kalibrasyon gönderilemedi: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const testPrint = async (qty) => {
    setBusy(true);
    try {
      const r = await printLabelsZebra(design, [{ ...zebraTestLabel(design), qty }]);
      toast.success(`${r.count} test etiketi gönderildi`);
    } catch (e) {
      toast.error(`Baskı gönderilemedi: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const btn = {
    padding: '6px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: busy ? 'wait' : 'pointer',
    background: T.panelLight, color: T.text, border: `1px solid ${T.borderLight}`,
  };
  const statusText = status === null ? 'Kontrol ediliyor...'
    : status.ok ? `Bağlı: ${status.printer?.name || 'Zebra yazıcı'}`
    : status.reason === 'disabled' ? 'Kapalı — etiketler normal yazdırma ile basılır'
    : status.reason === 'no-printer' ? 'Qubite Print açık ama Zebra yazıcı bulunamadı'
    : 'Qubite Print bu bilgisayarda bulunamadı — etiketler normal yazdırma ile basılır';

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Printer size={13} color={T.textSec} />
        <span style={{ fontSize: '11px', fontWeight: 600, color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zebra Doğrudan Baskı (bu bilgisayar)</span>
        <span style={{ fontSize: '11px', color: status?.ok ? '#4ade80' : T.textDim }}>{statusText}</span>
        <button type="button" style={btn} onClick={refresh}>Yenile</button>
        {status && !status.ok && status.reason === 'no-browser-print' && (
          <a href={PRINT_APP_DOWNLOAD_URL} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: T.accent }}>Qubite Print indir</a>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: '110px' }}>
          <label style={labelStyle}>Kullanım</label>
          <select value={settings.enabled ? '1' : '0'} onChange={(e) => { update({ enabled: e.target.value === '1' }); setTimeout(refresh, 0); }} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="1">Açık</option>
            <option value="0">Kapalı</option>
          </select>
        </div>
        <div style={{ minWidth: '130px' }}>
          <label style={labelStyle}>Yazıcı Modeli</label>
          <select value={PRINTER_MODELS[settings.model] ? settings.model : 'zd220'} onChange={(e) => update({ model: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            {Object.entries(PRINTER_MODELS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </div>
        {printers.length > 1 && (
          <div style={{ minWidth: '180px' }}>
            <label style={labelStyle}>Yazıcı</label>
            <select value={settings.printerUid || ''} onChange={(e) => { update({ printerUid: e.target.value }); setTimeout(refresh, 0); }} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">Varsayılan</option>
              {printers.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
            </select>
          </div>
        )}
        <div style={{ minWidth: '110px' }}>
          <label style={labelStyle}>Yatay İnce Ayar (mm)</label>
          <input type="number" step="0.125" value={settings.offsetXmm ?? 0}
            onChange={(e) => { const v = parseFloat(e.target.value); update({ offsetXmm: Number.isFinite(v) ? v : 0 }); }}
            style={inputStyle} />
        </div>
        <div style={{ minWidth: '110px' }}>
          <label style={labelStyle}>Dikey İnce Ayar (mm)</label>
          <input type="number" step="0.125" value={settings.offsetYmm ?? 0}
            onChange={(e) => { const v = parseFloat(e.target.value); update({ offsetYmm: Number.isFinite(v) ? v : 0 }); }}
            style={inputStyle} />
        </div>
        <button type="button" style={btn} disabled={busy || !status?.ok} onClick={calibrate}>Kalibre Et</button>
        <button type="button" style={btn} disabled={busy || !status?.ok} onClick={() => testPrint(1)}>Test Bas (1)</button>
        <button type="button" style={btn} disabled={busy || !status?.ok} onClick={() => testPrint(20)}>Toplu Test (20)</button>
      </div>
      {preview && (
        <div>
          <label style={labelStyle}>Yazıcıya giden görüntü (203 dpi, birebir)</label>
          <img src={preview} alt="Zebra önizleme" style={{ width: `${design.dimensions.labelWidth * 6}px`, maxWidth: '100%', imageRendering: 'pixelated', background: '#fff', border: `1px solid ${T.border}` }} />
        </div>
      )}
      <span style={{ fontSize: '10px', color: T.textDim, lineHeight: 1.4 }}>
        Kurulum: Qubite Print'i indirip çift tıklayın, tarayıcı izin sorarsa onaylayın, sonra bir kez "Kalibre Et". Yazıcı Modeli ve ince ayar sadece bu bilgisayarın yazıcısına uygulanır (model seçince o modelin kayması kendiliğinden düzeltilir) (artı = sağa / aşağı, 0.125 mm = 1 nokta).
      </span>
    </div>
  );
}
